import { computed, type Ref, ref } from 'vue';

import { BRAND, USER } from '@/models/auth/permissions.enum';
import { PLATFORM_CONNECTION } from '@/models/platform/platform-connection.enum';
import { useIdentityStore } from '@/stores/identity';
import { usePlatformStore } from '@/stores/platform';
import type { Brand } from '@/types/auth.types';

export function useTikTokCreatorMarketplace(brand: Ref<Brand | null>) {
  const platformType = PLATFORM_CONNECTION.TIKTOK_CREATOR_MARKETPLACE;

  const platformStore = usePlatformStore();
  const identityStore = useIdentityStore();

  const brandToConnect = ref(brand.value ?? identityStore.currentBrand);
  const brandId = ref(brandToConnect.value?.id);

  const platformConnection = computed(() =>
    (
      platformStore.getPlatformConnection as (
        platform: unknown,
        options?: Record<string, unknown>,
      ) => unknown
    )(platformType, { brandId: brandId.value }),
  );
  // computed
  const hasValidPlatformConnection = computed(() =>
    (
      platformStore.isPlatformConnected as (
        platform: unknown,
        options?: Record<string, unknown>,
      ) => boolean
    )(platformType.value, { brandId: brandToConnect.value?.id }),
  );
  const canManageTTCM = computed(() => {
    const userCanAccessCreators = identityStore.guard(
      USER.CREATORS.CAN_ACCESS_CREATORS,
      brandToConnect.value,
    );
    const brandCanAccessCreators = identityStore.guard(
      BRAND.CREATORS.CAN_ACCESS_CREATORS,
      brandToConnect.value,
    );
    return brandCanAccessCreators && userCanAccessCreators;
  });
  const canEnableAccounts = computed(() => canManageTTCM.value);
  const canConnect = computed(() => canManageTTCM.value || hasValidPlatformConnection.value);
  const hasMonitoredTTCMAccount = computed(() => {
    const allAccounts = platformStore.accounts as Record<
      string,
      Record<number, Array<{ isMonitored: boolean }>>
    >;
    const accounts =
      (brandId.value != null ? allAccounts[platformType.value]?.[brandId.value] : null) || [];
    return accounts.some((account) => account.isMonitored);
  });

  return {
    brandToConnect,
    brandId,
    platformConnection,
    hasMonitoredTTCMAccount,
    hasValidPlatformConnection,
    canManageTTCM,
    canEnableAccounts,
    canConnect,
  };
}
